import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { BLOCK_CAP } from '../lib/voxel-select';
import { volumeSummary } from '../lib/volume-stats';

const btn = 'flex-1 px-1 py-1 rounded text-[10px] cursor-pointer border';

/**
 * 3D model section of the Volume Tools: model picker, Blocks | Points, outside opacity of the visible
 * range, the volume readout and XYZ export. `sel` is the selection of the chosen model (lib/voxel-select.js).
 */
export default function VoxelVolumeControls({
  models, modelId, setModelId, renderMode, setRenderMode, outsideOpacity, setOutsideOpacity, sel, stats, canBlocks, onExportXyz,
}) {
  const { C } = useTheme();
  if (!models.length) return null;
  const capped = !!sel?.capped;
  const usedPoints = renderMode === 'blocks' && sel && sel.mode === 'points';
  return (
    <div data-testid="voxel-volume-controls">
      {models.length > 1 && (
        <select value={modelId || models[0].id} onChange={(e) => setModelId(e.target.value)} aria-label="Volume model"
          className="w-full text-[10px] mb-1 rounded px-1 py-1" style={{ background: C.bg3, color: C.text1, border: `1px solid ${C.border}` }}>
          {models.map((m) => <option key={m.id} value={m.id}>{m.model?.name || m.id}</option>)}
        </select>
      )}
      <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Render</div>
      <div className="flex gap-1 mb-1" role="group" aria-label="Render mode">
        {[['blocks', 'Blocks'], ['points', 'Points']].map(([id, label]) => (
          <button key={id} type="button" data-testid={`render-${id}`} aria-pressed={renderMode === id}
            disabled={id === 'blocks' && !canBlocks} onClick={() => setRenderMode(id)}
            className={`${btn} disabled:opacity-40 ${renderMode === id ? 'bg-accent-soft text-accent border-accent-soft' : 'border-transparent'}`}
            style={renderMode === id ? undefined : { background: C.bg3, color: C.text2 }}>{label}</button>
        ))}
      </div>
      {!canBlocks && <div className="text-[10px] mb-1" style={{ color: C.text3 }}>This model has no cell extents: drawn as points.</div>}
      {capped && usedPoints && (
        <div data-testid="blocks-capped-note" className="text-[10px] mb-1 text-amber-400">
          More than {BLOCK_CAP.toLocaleString()} cells: drawn as points.
        </div>
      )}
      <div className="text-[10px]" style={{ color: C.text2 }}>Outside range opacity: {(outsideOpacity * 100).toFixed(0)}%</div>
      <input type="range" aria-label="Outside range opacity" min={0} max={1} step={0.05} value={outsideOpacity}
        onChange={(e) => setOutsideOpacity(parseFloat(e.target.value))} className="w-full accent-cyan-400" />
      <div data-testid="volume-readout" className="text-[10px] my-1" style={{ color: C.text1 }}
        title="Counts only the cells that pass the DOI mask, slice and crop filters">
        {volumeSummary(stats)}
      </div>
      <button type="button" disabled={!sel?.popCells.length} onClick={onExportXyz} className={`${btn} w-full disabled:opacity-40`}
        title="x, y, z, resistivity (and lon, lat) of the visible cells, as CSV"
        style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>Export XYZ</button>
    </div>
  );
}
